import { useCallback, useMemo, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { MAX_ITEMS_TO_SHOW } from '../../../ui/components/draggable-item-list-provider';
import { useUnifiedAnalyticsContext } from '../../providers/unified-analytics-data-provider';
import type { SwitcherItemType } from '../links';
import { NAVIGATION_CHANNEL, UI_EVENT_TYPE } from './index';

export const UNIFIED_INSTRUMENTATION_SOURCE = 'topNav';
export const UNDEFINED_ORG = 'unknown_org';
export const UNDEFINED_UNIT = 'unknown_unit';
const UNDEFINED_PRODUCT = 'UNDEFINED_PRODUCT';
const UNDEFINED_SITE = 'unknown_site';

export type GROUP_IDENTIFIER = '1pApp' | '3pApp' | 'discoverMore' | 'customLink';
export type REORDER_OPTIONS = 'Move to top' | 'Move to bottom' | 'Move up' | 'Move down';

export const GROUP_INDEX_MAP: Record<GROUP_IDENTIFIER, number> = {
	'1pApp': 0,
	'3pApp': 1,
	discoverMore: 2,
	customLink: 3,
};

interface MenuStateChangedAttributes {
	itemState: 'expanded' | 'collapsed' | 'taskCompleted';
}

interface MenuViewedAttributes {
	firstPartyProducts: SwitcherItemType[];
	hasDiscoverySection: boolean;
	isOrderCustomized: boolean;
}

interface OrgDropdownViewedAttributes {
	expandOrgCount: number;
	expandOrgList: string[];
}

interface OrgListItemClickedAttributes {
	expandOrgCount: number;
	expandOrgId: string;
	expandOrgIndex: number;
}

interface UnitListItemClickedAttributes {
	expandOrgId: string;
	expandUnitCount: number;
	expandUnitId: string;
	expandUnitIndex: number;
}

interface SwitcherItemExpandClickedAttributes {
	groupIdentifier: GROUP_IDENTIFIER;
	groupItemCount: number;
	groupItemIndex: number;
	groupItemProduct?: string;
	isInMore: boolean;
}

interface SwitcherItemExpandListViewedAttributes {
	expandItemListCount: number;
	expandItemListSiteIds: string[];
	groupIdentifier: GROUP_IDENTIFIER;
	groupItemCount: number;
	groupItemIndex: number;
	groupItemProduct?: string;
	isInMore: boolean;
}

interface SwitcherItemExpandItemClickedAttributes {
	expandItemCloudId?: string;
	expandItemListCount: number;
	expandItemListIndex: number;
	groupIdentifier: GROUP_IDENTIFIER;
	groupItemCount: number;
	groupItemIndex: number;
	groupItemProduct?: string;
	isInMore: boolean;
	isRightClick: boolean;
}

interface ItemClickedAttributes {
	groupIdentifier: GROUP_IDENTIFIER;
	groupItemCount: number;
	groupItemIndex: number;
	groupItemProduct?: string;
	isInMore: boolean;
	isRightClick: boolean;
}

interface ReorderDropdownClickedAttributes {
	groupIdentifier: GROUP_IDENTIFIER;
	groupItemCount: number;
	groupItemIndex: number;
	groupItemProduct?: string;
	isInMore: boolean;
}

interface ReorderDropdownViewedAttributes {
	groupIdentifier: GROUP_IDENTIFIER;
	groupItemCount: number;
	groupItemIndex: number;
	groupItemProduct?: string;
	isInMore: boolean;
	reorderOptionsAvailable: REORDER_OPTIONS[];
}

interface ReorderDropdownItemClickedAttributes {
	groupIdentifier: GROUP_IDENTIFIER;
	groupItemCount: number;
	groupItemIndexAfterReorder: number;
	groupItemIndexBeforeReorder: number;
	groupItemProduct?: string;
	isInMoreAfterReorder: boolean;
	isInMoreBeforeReorder: boolean;
	reorderOptionIdentifier: REORDER_OPTIONS;
}
interface OverflowButtonClickedAttributes {
	buttonType: 'More' | 'Less';
}

/**
 * Pin/unpin of an org or unit in the org-unit selector.
 */
interface OrgUnitPinClickedAttributes {
	entityAri: string;
	entityType: 'org' | 'unit';
	isPinned: boolean;
	pinnedCount: number;
}

interface OrgUnitOverflowButtonClickedAttributes {
	buttonType: 'More' | 'Less';
	numHiddenItems: number;
}

interface ItemDraggedAttributes {
	groupIdentifier: GROUP_IDENTIFIER;
	groupItemCount: number;
	groupItemIndexAfterReorder: number;
	groupItemIndexBeforeReorder: number;
	groupItemProduct?: string;
	isInMoreAfterReorder: boolean;
	isInMoreBeforeReorder: boolean;
}

export interface MenuScrolledAttributes {
	isCurrentOrg: boolean;
	isCurrentUnit: boolean;
}

export interface RovoHomeExperimentExposedAttributes {
	cloudId?: string;
	selectedOrg?: string;
	selectedUnit?: string;
}

interface UnifiedAnalyticsEvents {
	// Switcher item clicked event when a switcher item is clicked (1P, 3P, customLink)
	fireItemClicked: (attributes: ItemClickedAttributes) => void;
	// Switcher item dragged event when an item has completed dragging
	fireItemDragged: (attributes: ItemDraggedAttributes) => void;
	// Scrolled event when the user scrolls the switcher menu
	fireMenuScrolled: (attributes: MenuScrolledAttributes) => void;
	// Switcher expanded or collapsed event via switcher button or navigation actions
	fireMenuStateChanged: (attributes: MenuStateChangedAttributes) => void;
	// Switcher menu viewed event when switcher is opened
	fireMenuViewed: (attributes: MenuViewedAttributes) => void;
	// Organisation dropdown (top selector) clicked
	fireOrgDropdownClicked: () => void;
	// Organisation dropdown viewed (when the dropdown list is rendered)
	fireOrgDropdownViewed: (attributes: OrgDropdownViewedAttributes) => void;
	// Organisation list item clicked inside the expanded dropdown
	fireOrgListItemClicked: (attributes: OrgListItemClickedAttributes) => void;
	// Org/unit selector overflow button clicked, revealing or hiding the unpinned rows
	fireOrgUnitOverflowButtonClicked: (attributes: OrgUnitOverflowButtonClickedAttributes) => void;
	// Org/unit pin button clicked, pinning or unpinning a row in the org/unit selector
	fireOrgUnitPinClicked: (attributes: OrgUnitPinClickedAttributes) => void;
	// Switcher overflow button clicked event when a user clicks the 'More' or 'Less' overflow button
	fireOverflowButtonClicked: (attributes: OverflowButtonClickedAttributes) => void;
	// Switcher item reorder dropdown clicked event
	fireReorderDropdownClicked: (attributes: ReorderDropdownClickedAttributes) => void;
	// Switcher item reorder dropdown list item clicked event
	fireReorderDropdownItemClicked: (attributes: ReorderDropdownItemClickedAttributes) => void;
	// Switcher item reorder dropdown list viewed event
	fireReorderDropdownViewed: (attributes: ReorderDropdownViewedAttributes) => void;
	// Rovo Home icon and label experiment exposure, when the selected org or unit changes to an enrolled site
	fireRovoHomeExperimentExposed: (attributes: RovoHomeExperimentExposedAttributes) => void;
	// Switcher item expand clicked
	fireSwitcherItemExpandClicked: (attributes: SwitcherItemExpandClickedAttributes) => void;
	// Switcher item expand item clicked (when a site is clicked from the expanded list)
	fireSwitcherItemExpandItemClicked: (attributes: SwitcherItemExpandItemClickedAttributes) => void;
	// Switcher item expand list viewed (when the list of sites is shown)
	fireSwitcherItemExpandListViewed: (attributes: SwitcherItemExpandListViewedAttributes) => void;
	// Unit list item clicked inside the org/unit switcher
	fireUnitListItemClicked: (attributes: UnitListItemClickedAttributes) => void;
}

const baseAttributes = {
	navLayer: 'global',
	navContainer: 'top',
	navVersion: 4,
};

export function useUnifiedAnalytics(): UnifiedAnalyticsEvents {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const {
		productSwitcher,
		hasDiscoverySection,
		thirdPartyConfig,
		currentOrg,
		currentUnit,
		organisations,
		customLinks,
		cloudId,
		tryRovoChatCohort,
		rovoHomeIconLabelExpValue,
	} = useUnifiedAnalyticsContext();
	const hasThirdPartySection = (thirdPartyConfig?.length ?? 0) > 0;
	// Track if menu viewed event has already fired (fire once per mount)
	const hasFiredMenuViewed = useRef<boolean>(false);

	const getGroupIndex = useCallback(
		(identifier: GROUP_IDENTIFIER): number => {
			const baseIndex = GROUP_INDEX_MAP[identifier];
			// For customLink group, if there is 3p section but no discovery section, shift up one position
			if (identifier === 'customLink' && hasThirdPartySection && !hasDiscoverySection) {
				return baseIndex - 1;
			}
			// For customLink group, if there is no 3p section and no discovery section, shift up two positions
			else if (identifier === 'customLink' && !hasThirdPartySection && !hasDiscoverySection) {
				return baseIndex - 2;
			}
			return baseIndex;
		},
		[hasDiscoverySection, hasThirdPartySection],
	);

	const topLevelAttributes = useMemo(
		() => ({
			actionSubject: 'atlassianSwitcher',
			eventType: 'ui',
			source: 'topNav',
			product: productSwitcher,
			tags: ['navigationExperiences'],
		}),
		[productSwitcher],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/91979
	const fireMenuStateChanged = useCallback(
		(attributes: MenuStateChangedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				eventType: UI_EVENT_TYPE,
				action: 'changed',
				actionSubjectId: 'atlassianSwitcherMenuState',
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(NAVIGATION_CHANNEL);
		},
		[createAnalyticsEvent, topLevelAttributes],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/92754
	const fireOrgDropdownClicked = useCallback(() => {
		createAnalyticsEvent({
			...topLevelAttributes,
			eventType: UI_EVENT_TYPE,
			action: 'clicked',
			actionSubjectId: 'atlassianSwitcherOrgExpand',
			attributes: {
				...baseAttributes,
			},
		}).fire(NAVIGATION_CHANNEL);
	}, [createAnalyticsEvent, topLevelAttributes]);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/92276
	const fireOrgDropdownViewed = useCallback(
		(attributes: OrgDropdownViewedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				eventType: UI_EVENT_TYPE,
				action: 'viewed',
				actionSubjectId: 'atlassianSwitcherOrgExpandList',
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(NAVIGATION_CHANNEL);
		},
		[createAnalyticsEvent, topLevelAttributes],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/92214
	const fireOrgListItemClicked = useCallback(
		(attributes: OrgListItemClickedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				eventType: UI_EVENT_TYPE,
				action: 'clicked',
				actionSubjectId: 'atlassianSwitcherOrgExpandItem',
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(NAVIGATION_CHANNEL);
		},
		[createAnalyticsEvent, topLevelAttributes],
	);

	const fireUnitListItemClicked = useCallback(
		(attributes: UnitListItemClickedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				eventType: UI_EVENT_TYPE,
				action: 'clicked',
				actionSubjectId: 'atlassianSwitcherUnitExpandItem',
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(NAVIGATION_CHANNEL);
		},
		[createAnalyticsEvent, topLevelAttributes],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/92343
	const fireSwitcherItemExpandClicked = useCallback(
		(attributes: SwitcherItemExpandClickedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				eventType: UI_EVENT_TYPE,
				action: 'clicked',
				actionSubjectId: 'atlassianSwitcherItemExpand',
				attributes: {
					...baseAttributes,
					...attributes,
					groupIndex: getGroupIndex(attributes.groupIdentifier),
					groupItemProduct: attributes.groupItemProduct ?? UNDEFINED_PRODUCT,
					groupItemSiteId: cloudId ?? UNDEFINED_SITE,
				},
			}).fire(NAVIGATION_CHANNEL);
		},
		[createAnalyticsEvent, topLevelAttributes, getGroupIndex, cloudId],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/92607
	const fireSwitcherItemExpandListViewed = useCallback(
		(attributes: SwitcherItemExpandListViewedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				eventType: UI_EVENT_TYPE,
				action: 'viewed',
				actionSubjectId: 'atlassianSwitcherItemExpandList',
				attributes: {
					...baseAttributes,
					...attributes,
					groupIndex: getGroupIndex(attributes.groupIdentifier),
					groupItemProduct: attributes.groupItemProduct ?? UNDEFINED_PRODUCT,
					groupItemSiteId: cloudId ?? UNDEFINED_SITE,
				},
			}).fire(NAVIGATION_CHANNEL);
		},
		[createAnalyticsEvent, topLevelAttributes, getGroupIndex, cloudId],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/92277
	const fireSwitcherItemExpandItemClicked = useCallback(
		(attributes: SwitcherItemExpandItemClickedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				eventType: UI_EVENT_TYPE,
				action: 'clicked',
				actionSubjectId: 'atlassianSwitcherItemExpandItem',
				attributes: {
					...baseAttributes,
					...attributes,
					groupIndex: getGroupIndex(attributes.groupIdentifier),
					groupItemProduct: attributes.groupItemProduct ?? UNDEFINED_PRODUCT,
					groupItemSiteId: cloudId ?? UNDEFINED_SITE,
					expandItemCloudId: attributes.expandItemCloudId ?? UNDEFINED_SITE,
				},
			}).fire(NAVIGATION_CHANNEL);
		},
		[createAnalyticsEvent, topLevelAttributes, getGroupIndex, cloudId],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/92268
	const fireMenuViewed = useCallback(
		(attributes: MenuViewedAttributes) => {
			if (hasFiredMenuViewed.current) {
				return;
			}

			const customLinksLength = customLinks?.length;
			const hasOverflow = attributes.firstPartyProducts.length > MAX_ITEMS_TO_SHOW;
			createAnalyticsEvent({
				...topLevelAttributes,
				eventType: UI_EVENT_TYPE,
				action: 'viewed',
				actionSubjectId: 'atlassianSwitcherMenu',
				attributes: {
					...baseAttributes,
					isOrderCustomized: attributes.isOrderCustomized,
					selectedOrg: currentOrg ?? UNDEFINED_ORG,
					currentOrg: currentOrg ?? UNDEFINED_ORG,
					selectedUnit: currentUnit ?? UNDEFINED_UNIT,
					currentUnit: currentUnit ?? UNDEFINED_UNIT,
					orgCount: organisations?.size ?? 0,
					orgList: Array.from(organisations?.keys() ?? []),
					tryRovoChatCohort,
					rovoHomeIconLabelExpValue,
					groupList: [
						{
							groupIdentifier: '1pApp',
							groupItemCount: hasOverflow
								? MAX_ITEMS_TO_SHOW
								: attributes.firstPartyProducts.length,
							groupItemList: hasOverflow
								? attributes.firstPartyProducts
										.slice(0, MAX_ITEMS_TO_SHOW)
										.map((item) => item.productType)
								: attributes.firstPartyProducts.map((item) => item.productType),
							showMoreButton: hasOverflow,
							inMoreItemCount: hasOverflow
								? attributes.firstPartyProducts.length - MAX_ITEMS_TO_SHOW
								: 0,
							inMoreItemList: hasOverflow
								? attributes.firstPartyProducts
										.slice(MAX_ITEMS_TO_SHOW)
										.map((item) => item.productType)
								: [],
						},
						...(hasThirdPartySection
							? [
									{
										groupIdentifier: '3pApp',
										groupItemCount: thirdPartyConfig?.length ?? 0,
										groupItemList: thirdPartyConfig?.map((item) => item.type) ?? [],
										showMoreButton: false,
										inMoreItemCount: 0,
										inMoreItemList: [],
									},
								]
							: []),
						...(hasDiscoverySection
							? [
									{
										groupIdentifier: 'discoverMore',
										groupItemCount: 1,
										groupItemList: ['moreAtlassianApps'],
										showMoreButton: false,
										inMoreItemCount: 0,
										inMoreItemList: [],
									},
								]
							: []),
						...((customLinksLength ?? 0) > 0
							? [
									{
										groupIdentifier: 'customLink',
										groupItemCount: customLinksLength,
										showMoreButton: false,
										inMoreItemCount: 0,
									},
								]
							: []),
					].map((group, index) => ({
						...group,
						groupIndex: index,
					})),
				},
			}).fire(NAVIGATION_CHANNEL);

			hasFiredMenuViewed.current = true;
		},
		[
			createAnalyticsEvent,
			topLevelAttributes,
			currentOrg,
			currentUnit,
			organisations,
			customLinks,
			thirdPartyConfig,
			hasThirdPartySection,
			hasDiscoverySection,
			tryRovoChatCohort,
			rovoHomeIconLabelExpValue,
		],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/114066
	const fireRovoHomeExperimentExposed = useCallback(
		(attributes: RovoHomeExperimentExposedAttributes) => {
			if (rovoHomeIconLabelExpValue === undefined) {
				return;
			}
			createAnalyticsEvent({
				...topLevelAttributes,
				eventType: UI_EVENT_TYPE,
				action: 'exposed',
				actionSubjectId: 'rovoHomeIconLabelExperiment',
				attributes: {
					...baseAttributes,
					selectedOrg: attributes.selectedOrg ?? UNDEFINED_ORG,
					selectedUnit: attributes.selectedUnit ?? UNDEFINED_UNIT,
					cloudId: attributes.cloudId ?? UNDEFINED_SITE,
					rovoHomeIconLabelExpValue,
				},
			}).fire(NAVIGATION_CHANNEL);
		},
		[createAnalyticsEvent, topLevelAttributes, rovoHomeIconLabelExpValue],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/92441
	const fireItemClicked = useCallback(
		(attributes: ItemClickedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				eventType: UI_EVENT_TYPE,
				action: 'clicked',
				actionSubjectId: 'atlassianSwitcherItem',
				attributes: {
					...baseAttributes,
					...attributes,
					groupIndex: getGroupIndex(attributes.groupIdentifier),
					groupItemProduct: attributes.groupItemProduct ?? UNDEFINED_PRODUCT,
					groupItemSiteId: cloudId ?? UNDEFINED_SITE,
					tryRovoChatCohort,
					rovoHomeIconLabelExpValue,
				},
			}).fire(NAVIGATION_CHANNEL);
		},
		[
			createAnalyticsEvent,
			topLevelAttributes,
			getGroupIndex,
			cloudId,
			tryRovoChatCohort,
			rovoHomeIconLabelExpValue,
		],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/92497
	const fireMenuScrolled = useCallback(
		(attributes: MenuScrolledAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				eventType: UI_EVENT_TYPE,
				action: 'scrolled',
				actionSubjectId: 'atlassianSwitcherMenu',
				attributes: {
					...baseAttributes,
					...attributes,
					selectedOrg: currentOrg ?? UNDEFINED_ORG,
					selectedUnit: currentUnit ?? UNDEFINED_UNIT,
				},
			}).fire(NAVIGATION_CHANNEL);
		},
		[createAnalyticsEvent, topLevelAttributes, currentOrg, currentUnit],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/92971
	const fireReorderDropdownClicked = useCallback(
		(attributes: ReorderDropdownClickedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				eventType: UI_EVENT_TYPE,
				action: 'clicked',
				actionSubjectId: 'atlassianSwitcherItemReorderDropdown',
				attributes: {
					...baseAttributes,
					...attributes,
					groupIndex: getGroupIndex(attributes.groupIdentifier),
					groupItemProduct: attributes.groupItemProduct ?? UNDEFINED_PRODUCT,
					groupItemSiteId: cloudId ?? UNDEFINED_SITE,
				},
			}).fire(NAVIGATION_CHANNEL);
		},
		[createAnalyticsEvent, topLevelAttributes, getGroupIndex, cloudId],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/92878
	const fireOverflowButtonClicked = useCallback(
		(attributes: OverflowButtonClickedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				eventType: UI_EVENT_TYPE,
				action: 'clicked',
				actionSubjectId: 'atlassianSwitcherOverflowButton',
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(NAVIGATION_CHANNEL);
		},
		[createAnalyticsEvent, topLevelAttributes],
	);

	const fireOrgUnitPinClicked = useCallback(
		(attributes: OrgUnitPinClickedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				eventType: UI_EVENT_TYPE,
				action: 'clicked',
				actionSubjectId: 'atlassianSwitcherOrgUnitPinButton',
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(NAVIGATION_CHANNEL);
		},
		[createAnalyticsEvent, topLevelAttributes],
	);

	const fireOrgUnitOverflowButtonClicked = useCallback(
		(attributes: OrgUnitOverflowButtonClickedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				eventType: UI_EVENT_TYPE,
				action: 'clicked',
				actionSubjectId: 'atlassianSwitcherOrgUnitOverflowButton',
				attributes: {
					...baseAttributes,
					...attributes,
				},
			}).fire(NAVIGATION_CHANNEL);
		},
		[createAnalyticsEvent, topLevelAttributes],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/92973
	const fireReorderDropdownViewed = useCallback(
		(attributes: ReorderDropdownViewedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				eventType: UI_EVENT_TYPE,
				action: 'viewed',
				actionSubjectId: 'atlassianSwitcherItemReorderDropdownList',
				attributes: {
					...baseAttributes,
					...attributes,
					groupIndex: getGroupIndex(attributes.groupIdentifier),
					groupItemProduct: attributes.groupItemProduct ?? UNDEFINED_PRODUCT,
					groupItemSiteId: cloudId ?? UNDEFINED_SITE,
				},
			}).fire(NAVIGATION_CHANNEL);
		},
		[createAnalyticsEvent, topLevelAttributes, getGroupIndex, cloudId],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/92975
	const fireReorderDropdownItemClicked = useCallback(
		(attributes: ReorderDropdownItemClickedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				eventType: UI_EVENT_TYPE,
				action: 'clicked',
				actionSubjectId: 'atlassianSwitcherItemReorderDropdownListItem',
				attributes: {
					...baseAttributes,
					...attributes,
					groupIndex: getGroupIndex(attributes.groupIdentifier),
					groupItemProduct: attributes.groupItemProduct ?? UNDEFINED_PRODUCT,
					groupItemSiteId: cloudId ?? UNDEFINED_SITE,
					tryRovoChatCohort,
					rovoHomeIconLabelExpValue,
				},
			}).fire(NAVIGATION_CHANNEL);
		},
		[
			createAnalyticsEvent,
			topLevelAttributes,
			getGroupIndex,
			cloudId,
			tryRovoChatCohort,
			rovoHomeIconLabelExpValue,
		],
	);

	// Data portal link: https://data-portal.internal.atlassian.com/analytics/registry/92879
	const fireItemDragged = useCallback(
		(attributes: ItemDraggedAttributes) => {
			createAnalyticsEvent({
				...topLevelAttributes,
				eventType: UI_EVENT_TYPE,
				action: 'dragged',
				actionSubjectId: 'atlassianSwitcherItem',
				attributes: {
					...baseAttributes,
					...attributes,
					groupIndex: getGroupIndex(attributes.groupIdentifier),
					groupItemProduct: attributes.groupItemProduct ?? UNDEFINED_PRODUCT,
					groupItemSiteId: cloudId ?? UNDEFINED_SITE,
					tryRovoChatCohort,
					rovoHomeIconLabelExpValue,
				},
			}).fire(NAVIGATION_CHANNEL);
		},
		[
			createAnalyticsEvent,
			topLevelAttributes,
			getGroupIndex,
			cloudId,
			tryRovoChatCohort,
			rovoHomeIconLabelExpValue,
		],
	);

	return {
		fireItemClicked,
		fireItemDragged,
		fireMenuStateChanged,
		fireMenuViewed,
		fireMenuScrolled,
		fireOrgDropdownClicked,
		fireOrgDropdownViewed,
		fireOrgListItemClicked,
		fireUnitListItemClicked,
		fireOrgUnitPinClicked,
		fireOrgUnitOverflowButtonClicked,
		fireOverflowButtonClicked,
		fireReorderDropdownClicked,
		fireReorderDropdownItemClicked,
		fireReorderDropdownViewed,
		fireRovoHomeExperimentExposed,
		fireSwitcherItemExpandClicked,
		fireSwitcherItemExpandListViewed,
		fireSwitcherItemExpandItemClicked,
	};
}
